// ═══════════════════════════════════════════════════════════════
// PACE — Tier Card Component (edit/view a single PACE tier)
// ═══════════════════════════════════════════════════════════════

import { useState } from 'react';
import {
  Paper, Badge, Group, Stack, Box, Text, TextInput, Select,
  Textarea, Button, ActionIcon,
} from '@mantine/core';
import { formatDistanceToNow } from 'date-fns';
import { formatPhone, isPhoneMethod } from '../../../lib/phoneFormat';
import type { PaceTier, CommMethod, PaceProfile } from '../../../store/paceStore';
import { PACE_TIER_LABELS, COMM_METHOD_LABELS } from '../../../store/paceStore';
import { IconCheck, IconPencil, IconPlus, IconX,
  IconAlertTriangle, IconCircleCheck } from '@tabler/icons-react';

interface Props {
  tier: PaceTier;
  entry?: PaceProfile;
  readOnly?: boolean;
  onSave?: (tier: PaceTier, data: {
    method: CommMethod; contactValue: string; backupValue: string; notes: string;
  }) => Promise<void>;
}

const TIER_COLORS: Record<PaceTier, { bg: string; border: string; badgeBg: string; badgeText: string; header: string }> = {
  primary:    { bg: '#f0fdf4', border: '#bbf7d0', badgeBg: '#dcfce7', badgeText: '#166534', header: '#166534' },
  alternate:  { bg: '#eff6ff', border: '#bfdbfe', badgeBg: '#dbeafe', badgeText: '#1e40af', header: '#1e40af' },
  contingency: { bg: '#fefce8', border: '#fef08a', badgeBg: '#fef9c3', badgeText: '#854d0e', header: '#854d0e' },
  emergency:  { bg: '#fef2f2', border: '#fecaca', badgeBg: '#fee2e2', badgeText: '#991b1b', header: '#991b1b' },
};

const TIER_LETTERS: Record<PaceTier, string> = { primary: 'P', alternate: 'A', contingency: 'C', emergency: 'E' };
const COMM_METHODS = Object.entries(COMM_METHOD_LABELS) as [CommMethod, string][];

export default function PaceTierCard({ tier, entry, readOnly = false, onSave }: Props) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    method: (entry?.method ?? 'cell_phone') as CommMethod,
    contactValue: entry?.contactValue ?? '',
    backupValue: entry?.backupValue ?? '',
    notes: entry?.notes ?? '',
  });

  const colors = TIER_COLORS[tier];
  const lastVerifiedDate = entry?.lastVerified ? new Date(entry.lastVerified) : null;
  const isStale = lastVerifiedDate
    ? (Date.now() - lastVerifiedDate.getTime()) > 180 * 86400000
    : false;

  const handleSave = async () => {
    if (!form.method || !form.contactValue) return;
    setSaving(true);
    try {
      await onSave?.(tier, form);
      setEditing(false);
    } finally { setSaving(false); }
  };

  return (
    <Paper
      radius="md"
      style={{ background: colors.bg, border: `2px solid ${colors.border}`, overflow: 'hidden' }}
    >
      <Group justify="space-between" px="md" py="sm" style={{ borderBottom: `1px solid ${colors.border}` }}>
        <Group gap="sm">
          <Badge
            size="lg" radius="md" variant="filled"
            styles={{ root: { background: colors.badgeBg, color: colors.badgeText, fontWeight: 700, fontSize: 13, width: 32, height: 32, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' } }}
          >
            {TIER_LETTERS[tier]}
          </Badge>
          <Text fw={600} style={{ color: colors.header }}>{PACE_TIER_LABELS[tier]}</Text>
          {lastVerifiedDate && !isNaN(lastVerifiedDate.getTime()) && (
            <Text size="xs" c="dimmed">· Verified {formatDistanceToNow(lastVerifiedDate, { addSuffix: true })}</Text>
          )}
        </Group>
        <Group gap="xs">
          {entry && !isStale && <IconCircleCheck size={14} color="#22c55e" />}
          {entry && isStale && (
            <Group gap={4}>
              <IconAlertTriangle size={13} color="#d97706" />
              <Text size="xs" c="#d97706" fw={500}>Stale</Text>
            </Group>
          )}
          {!entry && !readOnly && <Text size="xs" c="dimmed" fw={500}>Not set</Text>}
          {!readOnly && !editing && (
            <ActionIcon variant="subtle" color="gray" size="sm" radius="md"
              onClick={() => { setForm({ method: (entry?.method ?? 'cell_phone') as CommMethod, contactValue: entry?.contactValue ?? '', backupValue: entry?.backupValue ?? '', notes: entry?.notes ?? '' }); setEditing(true); }}>
              {entry ? <IconPencil size={12} /> : <IconPlus size={12} />}
            </ActionIcon>
          )}
        </Group>
      </Group>

      <Box px="md" py="sm">
        {!editing ? (
          entry ? (
            <Stack gap="xs">
              <Group gap="xl">
                <Box>
                  <Text size="xs" c="dimmed" tt="uppercase" lts={0.4}>Method</Text>
                  <Text size="sm" fw={500}>{COMM_METHOD_LABELS[entry.method] || entry.method}</Text>
                </Box>
                <Box>
                  <Text size="xs" c="dimmed" tt="uppercase" lts={0.4}>Contact</Text>
                  <Text size="sm" fw={500}>{entry.contactValue}</Text>
                </Box>
                {entry.backupValue && (
                  <Box>
                    <Text size="xs" c="dimmed" tt="uppercase" lts={0.4}>Backup</Text>
                    <Text size="sm" fw={500}>{entry.backupValue}</Text>
                  </Box>
                )}
              </Group>
              {entry.notes && (
                <Box mt={4} px="sm" py="xs" style={{ background: 'rgba(255,255,255,0.6)', borderRadius: 8, fontStyle: 'italic' }}>
                  <Text size="sm" c="dimmed">{entry.notes}</Text>
                </Box>
              )}
            </Stack>
          ) : (
            <Text size="sm" c="dimmed" fs="italic">
              {readOnly ? 'No contact information provided.' : 'Click + to add contact information for this tier.'}
            </Text>
          )
        ) : (
          <Stack gap="sm">
            <Select
              label="Communication Method *" size="sm"
              value={form.method}
              onChange={(val) => setForm(f => ({ ...f, method: (val ?? 'cell_phone') as CommMethod }))}
              data={COMM_METHODS.map(([value, label]) => ({ value, label }))}
            />
            <TextInput
              label="Contact Value *" size="sm"
              value={form.contactValue}
              onChange={e => setForm(f => ({ ...f, contactValue: isPhoneMethod(f.method) ? formatPhone(e.target.value) : e.target.value }))}
              placeholder={isPhoneMethod(form.method) ? '(555) 867-5309' : 'e.g. Ch. 7 simplex, Room 201, user@email.com...'}
              type={isPhoneMethod(form.method) ? 'tel' : 'text'}
            />
            <TextInput
              label="Backup Contact (optional)" size="sm"
              value={form.backupValue}
              onChange={e => setForm(f => ({ ...f, backupValue: isPhoneMethod(f.method) ? formatPhone(e.target.value) : e.target.value }))}
              placeholder={isPhoneMethod(form.method) ? '(555) 867-5309' : 'Secondary contact info'}
              type={isPhoneMethod(form.method) ? 'tel' : 'text'}
            />
            <Textarea
              label="Notes / Context" size="sm"
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="Availability, special instructions..."
              rows={2}
            />
            <Group gap="xs" mt={4}>
              <Button onClick={handleSave} disabled={saving || !form.contactValue} size="sm"
                leftSection={<IconCheck size={13} />}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
              <Button variant="default" onClick={() => setEditing(false)} size="sm"
                leftSection={<IconX size={13} />}>
                Cancel
              </Button>
            </Group>
          </Stack>
        )}
      </Box>
    </Paper>
  );
}
